import { useState, type FormEvent } from 'react';
import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { loginAdmin } from '@/services/api';
import { useAdminI18n } from '@/contexts/AdminI18nContext';

interface AdminLoginPageProps {
  onLogin: (token: string) => void;
}

export default function AdminLoginPage({ onLogin }: AdminLoginPageProps) {
  const { t } = useAdminI18n();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const session = await loginAdmin(username, password);
      onLogin(session.token);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.login.invalidCredentials'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: 'var(--app-viewport-stable-height, 100dvh)',
        display: 'grid',
        placeItems: 'center',
        pt: 'var(--app-page-top-padding)',
        pr: 'calc(var(--app-page-inline-padding) + var(--app-safe-area-right))',
        pb: 'calc(var(--app-page-bottom-padding) + var(--app-safe-area-bottom))',
        pl: 'calc(var(--app-page-inline-padding) + var(--app-safe-area-left))',
      }}
    >
      <Card sx={{ maxWidth: 420, width: '100%' }}>
        <CardContent sx={{ p: 3 }}>
          <Stack alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
            <Box
              sx={{
                width: 58,
                height: 58,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'rgba(118, 244, 184, 0.14)',
                color: 'primary.main',
              }}
            >
              <LockIcon sx={{ fontSize: 30 }} />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5">{t('admin.login.title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('admin.login.subtitle')}
              </Typography>
            </Box>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack component="form" onSubmit={handleSubmit} spacing={2}>
            <TextField
              fullWidth
              label={t('admin.login.username')}
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoFocus
            />
            <TextField
              fullWidth
              label={t('admin.login.password')}
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <Button fullWidth variant="contained" type="submit" size="large" disabled={isSubmitting}>
              {isSubmitting ? t('admin.login.loggingIn') : t('admin.login.login')}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
